import { AxiosResponse } from 'axios';
import { CreateEmployeeData, InsertEmployeeFileData } from '../@types/employee';
import {
  Employee,
  EmployeeEpi,
  EpiDeadlineItem,
  Epi,
  EpiDelivery,
  GroupedEmployee,
  SectorEpiRequirement,
} from '../models/employees';
import { File } from '../models/file';
import { api } from '../providers/base-api';
import { PaginatedResponse } from '../utils/types/data/pagination';

export async function createEmployeeRequest(
  userId: number,
  data: CreateEmployeeData,
): Promise<AxiosResponse<Employee>> {
  const response = await api.post<Employee>(`/employees/${userId}`, data);

  return response;
}

export async function showEmployeeRequest(
  userId: number,
): Promise<AxiosResponse<Employee>> {
  const response = await api.get<Employee>(`/employees/${userId}`);

  return response;
}

export async function updateEmployeeRequest(
  userId: number,
  data: CreateEmployeeData,
): Promise<Employee> {
  const response = await api.put<Employee>(`/employees/${userId}`, data);

  return response.data;
}

export async function getEmployeeFiles(
  employeeId: number,
): Promise<AxiosResponse<File[]>> {
  const response = await api.get<File[]>(`/employees/${employeeId}/files`);

  return response;
}

export async function addEmployeeFile(
  employeeId: number,
  data: InsertEmployeeFileData,
): Promise<AxiosResponse<void>> {
  const response = await api.post<void>(`/employees/${employeeId}/files`, data);

  return response;
}

export async function listEpisGroupedRequest(query?: {
  employeeId?: number;
  epiId?: number;
  sector?: string;
  role?: string;
  hasNoEpi?: boolean;
  hasPendingDelivery?: boolean;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<AxiosResponse<PaginatedResponse<GroupedEmployee>>> {
  const response = await api.get<PaginatedResponse<GroupedEmployee>>(
    '/employees/epis/grouped',
    { params: query },
  );
  return response;
}

export async function listEpiDeadlinesRequest(): Promise<
  AxiosResponse<{ items: EpiDeadlineItem[] }>
> {
  const response = await api.get<{ items: EpiDeadlineItem[] }>(
    '/employees/epis/deadlines',
  );
  return response;
}

export async function listEpisRequest(query?: {
  employeeId?: number;
  epiId?: number;
  page?: number;
  limit?: number;
  isDelivered?: boolean;
}): Promise<AxiosResponse<PaginatedResponse<EmployeeEpi>>> {
  const response = await api.get<PaginatedResponse<EmployeeEpi>>(
    '/employees/epis/all',
    {
      params: query,
    },
  );

  return response;
}

export async function markEpiAsDeliveredRequest(
  id: number,
  body?: { reason?: string; quantity?: number; deliveredAt?: string },
): Promise<AxiosResponse<void>> {
  const response = await api.post<void>(`/employees/epis/deliver/${id}`, body);

  return response;
}

export async function listEpiDeliveriesRequest(query?: {
  employeeId?: number;
  epiId?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<AxiosResponse<PaginatedResponse<EpiDelivery>>> {
  const response = await api.get<PaginatedResponse<EpiDelivery>>(
    '/employees/epis/deliveries/all',
    {
      params: query,
    },
  );

  return response;
}

export async function deleteEpiDeliveryRequest(
  id: number,
): Promise<AxiosResponse<void>> {
  const response = await api.delete<void>(`/employees/epis/deliveries/${id}`);
  return response;
}

export async function listEpiCatalogRequest(): Promise<AxiosResponse<Epi[]>> {
  const response = await api.get<Epi[]>('/employees/epis/catalog');

  return response;
}

export async function createEpiCatalogRequest(
  data: Partial<Epi>,
): Promise<AxiosResponse<Epi>> {
  const response = await api.post<Epi>('/employees/epis/catalog', data);

  return response;
}

export async function updateEpiCatalogRequest(
  id: number,
  data: Partial<Epi>,
): Promise<AxiosResponse<Epi>> {
  const response = await api.put<Epi>(`/employees/epis/catalog/${id}`, data);

  return response;
}

export async function deleteEpiCatalogRequest(
  id: number,
): Promise<AxiosResponse<void>> {
  const response = await api.delete<void>(`/employees/epis/catalog/${id}`);

  return response;
}

export async function linkEpisRequest(data: {
  employeeIds: number[];
  epiIds: number[];
}): Promise<AxiosResponse<void>> {
  const response = await api.post<void>('/employees/epis/link', data);

  return response;
}

export async function unlinkEpiRequest(
  id: number,
): Promise<AxiosResponse<void>> {
  const response = await api.delete<void>(`/employees/epis/link/${id}`);

  return response;
}

export async function listSectorEpiMatrixRequest(): Promise<
  AxiosResponse<SectorEpiRequirement[]>
> {
  const response = await api.get<SectorEpiRequirement[]>(
    '/employees/epis/sector-matrix',
  );
  return response;
}

export async function setSectorEpiRequirementRequest(data: {
  sector: string;
  epiId: number;
  required: boolean;
}): Promise<AxiosResponse<SectorEpiRequirement>> {
  const response = await api.post<SectorEpiRequirement>(
    '/employees/epis/sector-matrix',
    data,
  );
  return response;
}

export async function deleteSectorEpiRequirementRequest(
  id: number,
): Promise<AxiosResponse<void>> {
  const response = await api.delete<void>(
    `/employees/epis/sector-matrix/${id}`,
  );
  return response;
}

export type EpiReportFilters = {
  startDate?: string;
  endDate?: string;
  employeeId?: number;
  sector?: string;
  epiId?: number;
  status?: 'all' | 'ok' | 'pendente' | 'vencido';
};

export type EpiReportDeliveryRow = {
  id: number;
  date: string;
  reason: string | null;
  quantity: number;
  employeeId: number;
  employeeName: string;
  sector: string | null;
  epiId: number;
  epiName: string;
  ca: string | null;
  category: string | null;
};

export type EpiReportComplianceRow = {
  employeeId: number;
  employeeName: string;
  sector: string | null;
  epiId: number;
  epiName: string;
  ca: string | null;
  replacementPeriodDays: number | null;
  controlType: string | null;
  lastDeliveredAt: string | null;
  status: 'ok' | 'pendente' | 'vencido';
};

export type EpiReportLifecycleRow = {
  employeeId: number;
  employeeName: string;
  sector: string | null;
  epiId: number;
  epiName: string;
  controlType: string | null;
  replacementPeriodDays: number | null;
  lastDeliveredAt: string | null;
  daysSince: number | null;
  nextDueDate: string | null;
  daysUntilDue: number | null;
  status: 'ok' | 'alerta' | 'vencido' | 'pendente' | 'sem-prazo';
};

export type EpiReportSummaryRow = {
  epiId: number;
  epiName: string;
  ca: string | null;
  category: string | null;
  totalDeliveries: number;
  totalEmployees: number;
};

export type EpiReportResult = {
  kpis: {
    totalDeliveries: number;
    totalEmployeesServed: number;
    pendingCompliance: number;
    needsReplacement: number;
  };
  deliveries: EpiReportDeliveryRow[];
  compliance: EpiReportComplianceRow[];
  lifecycle: EpiReportLifecycleRow[];
  summary: EpiReportSummaryRow[];
};

export async function fetchEpiReportsRequest(
  filters: EpiReportFilters,
): Promise<AxiosResponse<EpiReportResult>> {
  const params: Record<string, any> = {};
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  if (filters.employeeId) params.employeeId = filters.employeeId;
  if (filters.sector) params.sector = filters.sector;
  if (filters.epiId) params.epiId = filters.epiId;
  if (filters.status && filters.status !== 'all')
    params.status = filters.status;
  return api.get<EpiReportResult>('/employees/epis/reports', { params });
}
